<script setup lang="ts">
/**
 * ArticleBody — 게시판 본문(서식 HTML) 렌더 SSoT. 공지 상세·입시 소식 상세가 공유한다(083).
 *
 * ⚠️ v-html 예외 허용 지점: `html` 은 백엔드 저장 시점에 jsoup Safelist(`HtmlSanitizer`)로 sanitize 된 본문만 받는다.
 * 다른 사용자 입력을 이 컴포넌트에 넘기지 않는다(XSS 방지 원칙 — 게시판 본문만 명시적 예외).
 *
 * 표는 본문 폭을 넘으면 **표 안에서만** 가로 스크롤한다(페이지 가로 스크롤 금지 — 모바일 375px).
 */
defineProps<{
  /** 서버 sanitize 완료된 본문 HTML. */
  html: string;
}>();
</script>

<template>
  <!-- eslint-disable-next-line vue/no-v-html -->
  <div class="article-prose" v-html="html" />
</template>

<style scoped>
/*
 * TipTap 산출 태그 prose 스타일(디자인 토큰 기반, typography 플러그인 미도입).
 * 허용 태그는 서버 Safelist 가 SSoT — 여기서는 렌더만 책임진다.
 * BO 에디터(frontend/apps/web-ipsi/src/components/RichTextEditor)가 같은 크기·간격 비율로 편집 화면을 그린다
 * — 비율을 바꾸면 BO 쪽도 맞춘다(운영자가 결과를 보면서 쓰기 위함).
 */
.article-prose {
  font-size: 14.5px;
  line-height: 1.75;
  color: var(--body);
  overflow-wrap: anywhere;
}

.article-prose :deep(p) {
  margin: 0 0 1em;
}

.article-prose :deep(p:last-child) {
  margin-bottom: 0;
}

.article-prose :deep(strong) {
  font-weight: 700;
  color: var(--brand);
}

.article-prose :deep(em) {
  font-style: italic;
}

.article-prose :deep(u) {
  text-decoration: underline;
}

.article-prose :deep(s) {
  text-decoration: line-through;
}

.article-prose :deep(h1),
.article-prose :deep(h2),
.article-prose :deep(h3),
.article-prose :deep(h4) {
  margin: 1.4em 0 0.6em;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--brand);
}

.article-prose :deep(h1) {
  font-size: 1.35em;
}

.article-prose :deep(h2) {
  font-size: 1.2em;
}

.article-prose :deep(h3) {
  font-size: 1.1em;
}

.article-prose :deep(h4) {
  font-size: 1em;
}

.article-prose :deep(h1:first-child),
.article-prose :deep(h2:first-child),
.article-prose :deep(h3:first-child),
.article-prose :deep(h4:first-child) {
  margin-top: 0;
}

.article-prose :deep(ul),
.article-prose :deep(ol) {
  margin: 0 0 1em;
  padding-left: 1.4em;
}

.article-prose :deep(ul) {
  list-style: disc;
}

.article-prose :deep(ol) {
  list-style: decimal;
}

.article-prose :deep(li) {
  margin: 0.3em 0;
}

.article-prose :deep(blockquote) {
  margin: 1em 0;
  padding: 0.6em 1em;
  border-left: 3px solid var(--accent-line);
  background: var(--accent-soft);
  color: var(--brand);
  border-radius: 6px;
}

.article-prose :deep(code) {
  padding: 0.15em 0.4em;
  border-radius: 4px;
  background: var(--bg-2);
  font-size: 0.92em;
  font-family: ui-monospace, 'SF Mono', Menlo, monospace;
}

.article-prose :deep(pre) {
  margin: 1em 0;
  padding: 0.9em 1em;
  border-radius: 8px;
  background: var(--bg-2);
  overflow-x: auto;
}

.article-prose :deep(pre code) {
  padding: 0;
  background: transparent;
}

.article-prose :deep(a) {
  color: var(--brand-muted);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.article-prose :deep(a:hover) {
  color: var(--accent-deep);
}

.article-prose :deep(hr) {
  margin: 1.6em 0;
  border: none;
  border-top: 1px solid var(--line);
}

/* 표 — 셀 단위 줄바꿈 대신 표 자체를 가로 스크롤 영역으로 만든다(래퍼 DOM 없이 display:block). */
.article-prose :deep(table) {
  display: block;
  max-width: 100%;
  margin: 1em 0;
  overflow-x: auto;
  border-collapse: collapse;
  font-size: 0.93em;
}

.article-prose :deep(th),
.article-prose :deep(td) {
  padding: 0.5em 0.75em;
  border: 1px solid var(--line);
  white-space: nowrap;
  overflow-wrap: normal;
}

.article-prose :deep(th) {
  background: var(--bg-2);
  font-weight: 700;
  color: var(--brand);
}
</style>
